var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { mediaApi } from '@sitecore-jss/sitecore-jss/media';
import PropTypes from 'prop-types';
import React from 'react';
import { addClassName, convertAttributesToReactProps } from '../utils';
import { getAttributesString } from '../utils';
const getEditableWrapper = (editableMarkup, ...otherProps) => (
// create an inline wrapper and use dangerouslySetInnerHTML.
// if we try to parse the EE value, the parser will strip invalid or disallowed attributes from html elements - and EE uses several
React.createElement("span", Object.assign({ className: "sc-image-wrapper" }, otherProps, { dangerouslySetInnerHTML: { __html: editableMarkup } })));
const getImageAttrs = (_a, imageParams, mediaUrlPrefix) => {
    var { src, srcSet } = _a, otherAttrs = __rest(_a, ["src", "srcSet"]);
    if (!src) {
        return null;
    }
    addClassName(otherAttrs);
    const newAttrs = Object.assign({}, otherAttrs);
    // update image URL for jss handler and image rendering params
    const resolvedSrc = mediaApi.updateImageUrl(src, imageParams, mediaUrlPrefix);
    if (srcSet) {
        // replace with HTML-formatted srcset, including updated image URLs
        newAttrs.srcSet = mediaApi.getSrcSet(resolvedSrc, srcSet, imageParams, mediaUrlPrefix);
    }
    // always output original src as fallback for older browsers
    newAttrs.src = resolvedSrc;
    return newAttrs;
};
/**
 * @param {ImageField} imageField {ImageField} provides the dynamicMedia which is used to render the image
 * @param {ImageProps.imageParams} imageParams {ImageProp['imageParams']}} provides the image parameters that will be attached to the image URL
 * @param {RegExp} mediaUrlPrefix {RegExp} the url prefix regex used in the mediaApi
 * @param {ImageProps} otherProps {ImageProps} all other props included on the image component
 * @returns Experience Editor Markup
 */
export const getEEMarkup = (imageField, imageParams, mediaUrlPrefix, otherProps) => {
    // we likely have an experience editor value, should be a string
    const foundImg = mediaApi.findEditorImageTag(imageField.editable);
    if (!foundImg) {
        return getEditableWrapper(imageField.editable);
    }
    const foundImgProps = convertAttributesToReactProps(foundImg.attrs);
    // Note: otherProps may override values from foundImgProps, e.g. `style`, `className` prop
    // We do not attempt to merge.
    const imgAttrs = getImageAttrs(Object.assign(Object.assign({}, foundImgProps), otherProps), imageParams, mediaUrlPrefix);
    if (!imgAttrs) {
        return getEditableWrapper(imageField.editable);
    }
    const imgHtml = `<img ${getAttributesString(imgAttrs)} />`;
    const editableMarkup = imageField.editable.replace(foundImg.imgTag, imgHtml);
    return getEditableWrapper(editableMarkup);
};
export const Image = (_a) => {
    var { media, editable, imageParams, field, mediaUrlPrefix } = _a, otherProps = __rest(_a, ["media", "editable", "imageParams", "field", "mediaUrlPrefix"]);
    // allows the mistake of using 'field' prop instead of 'media' (consistent with other helpers)
    if (field && !media) {
        media = field;
    }
    const dynamicMedia = media;
    if (!media ||
        (!dynamicMedia.editable && !dynamicMedia.value && !dynamicMedia.src)) {
        return null;
    }
    const imageField = dynamicMedia;
    if (editable && imageField.editable) {
        return getEEMarkup(imageField, imageParams, mediaUrlPrefix, otherProps);
    }
    // some wise-guy/gal is passing in a 'raw' image object value
    const img = dynamicMedia.src
        ? media
        : dynamicMedia.value;
    if (!img) {
        return null;
    }
    const attrs = getImageAttrs(Object.assign(Object.assign({}, img), otherProps), imageParams, mediaUrlPrefix);
    if (attrs) {
        return React.createElement("img", Object.assign({}, attrs));
    }
    return null; // we can't handle the truth
};
Image.propTypes = {
    media: PropTypes.oneOfType([
        PropTypes.shape({
            src: PropTypes.string,
        }),
        PropTypes.shape({
            value: PropTypes.object,
            editable: PropTypes.string,
        }),
    ]),
    field: PropTypes.oneOfType([
        PropTypes.shape({
            src: PropTypes.string,
        }),
        PropTypes.shape({
            value: PropTypes.object,
            editable: PropTypes.string,
        }),
    ]),
    editable: PropTypes.bool,
    mediaUrlPrefix: PropTypes.instanceOf(RegExp),
    imageParams: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.number.isRequired, PropTypes.string.isRequired]).isRequired),
};
Image.defaultProps = {
    editable: true,
};
Image.displayName = 'Image';
